import React, { useContext } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { Link } from 'gatsby'
import { array, bool, number } from 'prop-types'
import { stripUnit } from 'polished'

import { pricify } from 'utils/pricify'

import * as spacing from 'styles/spacing'
import { white, primaryColor, black, grey, greyDark, borderColor } from 'styles/colors'
import { animationTime, animationCurve, navHeight, square, boxShadow, borderRadius, cover } from 'styles/global'
import { fontSizes, fontWeights } from 'styles/typography'
import { media } from 'styles/media'

import CheckoutContext from 'contexts/Checkout'
import Icon from 'components/Icon'
import Product from 'components/Nav/Product'
import Button from 'components/button/Button'
import ButtonGroup from 'components/button/ButtonGroup'

import CartIcon from 'icons/cart.svg'

const pulse = keyframes`
  0%, 100% {
    transform: scale3d(1, 1, 1);
  }
  50% {
    transform: scale3d(1.5, 1.5, 1.5);
  }
`

const Container = styled.div`
  position: relative;
  color: ${black};

  ${media.medium`
    margin-left: ${spacing.large};
  `};

  ${media.large`
    margin-left: ${spacing.xLarge};
  `};

  ${media.xLarge`
    margin-left: ${spacing.xxLarge};
  `};
`

const Trigger = styled(props => <Link {...props} />)`
  position: relative;
  display: flex;
  align-items: center;
  text-align: left;
  cursor: pointer;
  color: ${props => (props.transparent ? white : black)};
  transition: ${animationTime} ${animationCurve};

  &:hover:before {
    content: '';
    display: block;
    position: absolute;
    left: -${spacing.large};
    right: -${spacing.large};
    top: 0;
    bottom: 0;
    cursor: pointer;
  }

  ${media.medium`
    height: ${navHeight};
  `};
`

const IconWrap = styled.div`
  position: relative;
  display: flex;
  ${square('24px')};
`

const Quantity = styled.div`
  position: absolute;
  z-index: 1;
  bottom: -5px;
  left: -5px;
  line-height: 16px;
  text-align: center;
  color: ${white};
  font-weight: ${fontWeights.bold};
  font-size: 10px;
  letter-spacing: -0.5px;
  ${square`16px`};
`

const QuantityCircle = styled.div`
  background-color: ${primaryColor};
  border-radius: 50%;
  z-index: -1;
  ${cover('absolute')};

  ${props =>
    props.isAnimated &&
    css`
      animation: ${pulse} 0.7s ease-in-out;
    `};
`

const Cart = styled.div`
  background: ${white};
  margin-top: -${spacing.small};
  top: 100%;
  right: 0;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  position: absolute;
  transition: opacity ${animationTime} ${animationCurve}, visibility ${animationTime} ${animationCurve};
  width: 360px;
  z-index: 10;
  color: ${grey};
  border-radius: ${borderRadius};
  border: 1px solid ${borderColor};
  ${boxShadow};

  ${media.medium`
    ${Container}:hover &,
    ${Container}:focus & {
      opacity: 1;
      visibility: visible;
      pointer-events: all;
    }
  `};

  ${media.medium`
    ${props =>
      props.visible &&
      css`
        opacity: 1;
        visibility: visible;
        pointer-events: all;
      `}
  `};
`

export const Section = styled.section`
  padding: ${spacing.large};

  & + & {
    border-top: 1px solid ${borderColor};
  }
`

export const SectionHeading = styled.span`
  text-transform: uppercase;
  display: block;
  font-size: ${fontSizes.small};
  letter-spacing: 0.1em;
  margin-bottom: ${stripUnit(spacing.small) * 1.5 + 'px'};
  text-align: left;
  color: ${black};
`

export const Scroll = styled.div`
  max-height: 40vh;
  overflow-y: auto;
  border-bottom: 1px solid ${borderColor};
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${fontSizes.small};

  strong {
    color: ${greyDark};
  }

  & + & {
    margin-top: ${spacing.tiny};
  }

  &:first-child {
    margin-bottom: ${spacing.small};
  }
`

function TinyCart({ quantity, transparent }) {
  const {
    tinyCartVisible,
    setTinyCartVisible,
    priceBreakdown,
    subscriptionLineItems,
    subscriptionLineItemsQuantity,
    regularLineItems,
    regularLineItemsQuantity,
    hasNewItem
  } = useContext(CheckoutContext)
  return (
    <Container>
      <Trigger
        to="/cart"
        onMouseEnter={() => {
          setTinyCartVisible(true)
        }}
        onMouseLeave={() => {
          setTinyCartVisible(false)
        }}
        transparent={transparent}
        data-tracking="nav--link--image--Cart"
      >
        <IconWrap>
          <Quantity transparent={transparent}>
            {quantity > 0 ? quantity : 0}
            <QuantityCircle isAnimated={hasNewItem} />
          </Quantity>
          <Icon icon={<CartIcon />} />
        </IconWrap>
      </Trigger>
      {quantity === 0 ? (
        <Cart visible={tinyCartVisible}>
          <Section>Your cart is currently empty</Section>
        </Cart>
      ) : (
        <Cart visible={tinyCartVisible}>
          {(regularLineItems?.length > 0 || subscriptionLineItems?.length > 0) && (
            <Scroll>
              {/* Subscriptions */}
              {subscriptionLineItems?.length > 0 && (
                <Section>
                  <SectionHeading>
                    <span>Subscriptions</span>
                    <span> ({subscriptionLineItemsQuantity})</span>
                  </SectionHeading>

                  <div>
                    {subscriptionLineItems?.map((product, i) => (
                      <Product key={i} product={product.node} isSubscription />
                    ))}
                  </div>
                </Section>
              )}

              {/* Products */}
              {regularLineItems?.length > 0 && (
                <Section>
                  <SectionHeading>
                    <span>Purchases</span>
                    <span> ({regularLineItemsQuantity})</span>
                  </SectionHeading>

                  <div>
                    {regularLineItems?.map((product, i) => (
                      <Product key={i} product={product.node} />
                    ))}
                  </div>
                </Section>
              )}
            </Scroll>
          )}

          {/* Price */}
          {priceBreakdown && (
            <Section>
              <Row>
                <span>
                  <strong>Subtotal</strong>
                </span>
                <strong>
                  {pricify({
                    amount:
                      Number(priceBreakdown?.regular?.lineItemsSubtotalPrice) +
                      Number(priceBreakdown?.subscription?.lineItemsSubtotalPrice)
                  })}
                </strong>
              </Row>
              {regularLineItems?.length > 0 && (
                <Row>
                  <span>Purchases</span>
                  <span>
                    {pricify({
                      amount: Number(priceBreakdown?.regular?.lineItemsSubtotalPrice)
                    })}
                  </span>
                </Row>
              )}
              {subscriptionLineItems?.length > 0 && (
                <Row>
                  <span>Subscriptions</span>
                  <span>
                    {pricify({
                      amount: Number(priceBreakdown?.subscription?.lineItemsSubtotalPrice)
                    })}
                    /month
                  </span>
                </Row>
              )}
            </Section>
          )}
          <Section>
            <ButtonGroup full>
              <Button to="/cart" theme="secondary" fullwidth>
                View cart
              </Button>
            </ButtonGroup>
          </Section>
        </Cart>
      )}
    </Container>
  )
}

TinyCart.propTypes = {
  quantity: number,
  products: array,
  transparent: bool
}

export default TinyCart
